import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { useGetUserReferral, useCompleteReferral } from '@/api/endpoints/user/useUserReferral';
import { useAuth } from '@/context/AuthProvider';

export const ReferralHandler: React.FC = () => {
  const history = useHistory();
  const { isAuthenticated, user } = useAuth();
  const { data: pendingReferral, isLoading } = useGetUserReferral();
  const completeReferralMutation = useCompleteReferral();
  const processingRef = useRef<string | null>(null); // Track processing to prevent duplicates

  useEffect(() => {
    const handleReferral = async () => {
      // Only check for referrals if user is authenticated
      if (!isAuthenticated || !user || isLoading) return;

      // Check if there's a pending referral that hasn't been processed
      // Also check if we're not already processing this referral
      if (
        pendingReferral &&
        !pendingReferral.is_redirected &&
        processingRef.current !== pendingReferral.id
      ) {
        // Check if we're in an onboarding flow
        const currentPath = window.location.pathname;
        const isInOnboarding = currentPath.includes('/onboarding');
        
        if (isInOnboarding) {
          // Store the referral URL for after onboarding
          localStorage.setItem('postOnboardingRedirect', pendingReferral.referral_url);
        } else {
          try {
            // Mark this referral as being processed
            processingRef.current = pendingReferral.id;
            
            // Mark the referral as completed
            await completeReferralMutation.mutateAsync();

            // Redirect to the original URL they were trying to access
            history.push(pendingReferral.referral_url);
          } catch (error) {
            // Failed to process referral, reset processing flag
            processingRef.current = null;
          }
        }
      }
    };

    handleReferral();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, pendingReferral?.id, isLoading, history]); // Intentionally omit mutations

  // This component doesn't render anything
  return null;
};